import { FlowStatus, ChainId } from '@/features/crosschain-buy/domain/flow'
import { TRANSACTION_EXPLORER_URL_BY_CHAIN_ID } from '@/features/crosschain-buy/shared/transaction-explorers'
import { buildJupiterStep, buildStonTonToSpottyStep, buildStonUsdtToTonStep } from './step-builders'
import type { RouteContext, RouteStep, RouteStrategy, StepStatus, TxLink } from './types'
import type { StepStatus as CanonicalStepStatus } from '@/features/crosschain-buy/domain/progress'

const CHAIN_NAMES: Record<number, string> = {
  42161: 'Arbitrum',
  1: 'Ethereum',
  8453: 'Base',
  10: 'Optimism',
}

const SYMBIOSIS_ACTIVE_STATUSES = new Set<FlowStatus>([
  FlowStatus.INTERMEDIATE_SYMBIOSIS_APPROVING,
  FlowStatus.INTERMEDIATE_SYMBIOSIS_SWAPPING,
  FlowStatus.INTERMEDIATE_SYMBIOSIS_TRACKING,
])

const displayStatus = (status: CanonicalStepStatus): StepStatus => {
  if (status === 'completed' || status === 'failed' || status === 'pending') return status
  return 'active'
}

class CompositeStrategy implements RouteStrategy {
  compute(ctx: RouteContext): RouteStep[] {
    const { state } = ctx
    const intermediateChain = ctx.intermediateEvmChainId ?? ChainId.ARBITRUM
    const chainName = CHAIN_NAMES[intermediateChain] ?? 'EVM'
    const evmUsdtAmount = ctx.evmUsdtAmount
    const status = state.status
    const steps: RouteStep[] = []

    if (!ctx.skipJupiterSwap) {
      const jupiterOutputLabel = ctx.jupiterUsdtOutput
        ? `~${ctx.jupiterUsdtOutput} USDT`
        : '... USDT'
      steps.push(
        buildJupiterStep(
          ctx.sourceAmount,
          ctx.fromToken,
          jupiterOutputLabel,
          state,
          ctx.step1Status,
          ctx.jupiterTxUrl,
          FlowStatus.SOLANA_JUPITER_SWAPPING
        )
      )
    }

    steps.push(this.buildLayerswapStep(ctx, status, chainName, evmUsdtAmount))
    steps.push(this.buildSymbiosisStep(ctx, status, chainName, intermediateChain))

    const stonCtx = {
      previewLoading: ctx.previewLoading,
      tonAmount: ctx.tonAmount,
      spottyAmount: ctx.spottyAmount,
      bridgeOutputAmount: ctx.bridgeOutputAmount,
    }
    steps.push(buildStonUsdtToTonStep(stonCtx, ctx.step2Status, ctx.tonUsdtToTonTxUrl))
    steps.push(buildStonTonToSpottyStep(stonCtx, ctx.step3Status, ctx.tonTxUrl))

    return steps
  }

  private buildLayerswapStep(
    ctx: RouteContext,
    status: FlowStatus,
    chainName: string,
    evmUsdtAmount: string
  ): RouteStep {
    const layerswapStep = ctx.state.steps.find(
      (step) => step.type === 'bridge' && step.provider === 'layerswap'
    )
    const layerswapDone = SYMBIOSIS_ACTIVE_STATUSES.has(status) || ctx.step1Status === 'completed'

    let layerswapStatus: StepStatus = 'pending'
    if (layerswapStep) layerswapStatus = displayStatus(layerswapStep.status)
    else if (layerswapDone) layerswapStatus = 'completed'
    else if (status === FlowStatus.SOLANA_LAYERSWAP_BRIDGING) layerswapStatus = 'active'
    else if (ctx.step1Status === 'failed') layerswapStatus = 'failed'

    const solanaBase = TRANSACTION_EXPLORER_URL_BY_CHAIN_ID[ChainId.SOLANA] ?? ''
    const txLinks: TxLink[] = layerswapStep?.txHash
      ? [{ url: `${solanaBase}${layerswapStep.txHash}`, label: 'View tx' }]
      : []

    return {
      key: 'bridge_layerswap',
      label: 'Bridge via Layerswap',
      amountLine: `USDT (Solana) → ~${evmUsdtAmount || '...'} USDT (${chainName})`,
      amountLoading: false,
      status: layerswapStatus,
      txLinks,
      etaHint:
        status === FlowStatus.SOLANA_LAYERSWAP_BRIDGING ? 'Bridge via Layerswap (~15 sec)' : null,
    }
  }

  private buildSymbiosisStep(
    ctx: RouteContext,
    status: FlowStatus,
    chainName: string,
    intermediateChain: number
  ): RouteStep {
    const symbiosisStep = ctx.state.steps.find(
      (step) => step.type === 'bridge' && step.provider === 'symbiosis'
    )
    const symbiosisActive = SYMBIOSIS_ACTIVE_STATUSES.has(status)
    const layerswapDone = SYMBIOSIS_ACTIVE_STATUSES.has(status) || ctx.step1Status === 'completed'

    let symbiosisStatus: StepStatus = 'pending'
    if (symbiosisStep) symbiosisStatus = displayStatus(symbiosisStep.status)
    else if (ctx.step1Status === 'completed') symbiosisStatus = 'completed'
    else if (symbiosisActive) symbiosisStatus = 'active'
    else if (ctx.step1Status === 'failed' && layerswapDone) symbiosisStatus = 'failed'

    const txLinks: TxLink[] = []
    if (symbiosisStep?.txHash) {
      const evmExplorer = TRANSACTION_EXPLORER_URL_BY_CHAIN_ID[intermediateChain]
      if (evmExplorer) {
        txLinks.push({
          url: `${evmExplorer}${symbiosisStep.txHash}`,
          label: 'View tx',
        })
      }
    }

    const bridgeOut = ctx.bridgeOutputAmount ? `~${ctx.bridgeOutputAmount}` : '...'
    return {
      key: 'bridge_symbiosis',
      label: 'Bridge via Symbiosis',
      amountLine: `USDT (${chainName}) → ${bridgeOut} USDT (TON)`,
      amountLoading: false,
      status: symbiosisStatus,
      txLinks,
      etaHint: symbiosisActive ? 'Cross-chain bridge may take 5-15 min' : null,
    }
  }
}

export const compositeStrategy = new CompositeStrategy()
